import {
  useGetFactorSingleLimitQuery,
  useDeleteGeneralAgreementMutation,
  useDeleteFactorLimitMutation,
  useGetGeneralAgreementV2Query,
} from '@/app/api';
import { BaseModalRef } from '@/app/helpers/types';
import { FactorChangeLimitModal } from '@/features/factor-change-limit-modal';
import { FactorLimitActions } from '@/features/factor-limit-actions';
import { FactorLimitActivities } from '@/features/factor-limit-activities';
import { CabinetWrapper } from '@/shared/cabinet-wrapper';
import { FormDatePicker } from '@/shared/inputs/form-date-picker';
import { FormTextInput } from '@/shared/inputs/form-text-input';
import { PageTitle } from '@/shared/page-title';
import { UIDefinitionList } from '@/shared/ui/ui-definition-list';
import { UIFormLabel } from '@/shared/ui/ui-form-label';
import { UILoader } from '@/shared/ui/ui-loader';
import UITabs from '@/shared/ui/ui-tabs';
import { Box, Button, Chip, Divider, Stack, Typography } from '@mui/material';
import { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { numericFormatter } from 'react-number-format';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getFetchEror } from '@/app/helpers';
import { useNotification } from '@/shared/contexts/NotificationContext';
import { eimzo } from '@/entities/eimzo';
import { useAppSelector } from '@/app/hooks';
import { DidoxPreviewModal } from '@/shared/modals/didox-preview-modal';
import { DidoxPreviewModalRef } from '@/shared/modals/didox-preview-modal/types';

const BlockComp: FC<{ title: string; value: string }> = ({ title, value }) => (
  <Box
    sx={{
      p: 2,
      bgcolor: 'rgba(21, 85, 252, 0.04)',
      borderRadius: '6px',
      flex: 1,
    }}
  >
    <UIFormLabel sx={{ color: 'rgba(11, 19, 112, .5)', fontSize: 13 }}>
      {title}:
    </UIFormLabel>
    <Typography fontSize={20} fontWeight={600}>
      {value}
    </Typography>
  </Box>
);

interface FormValues {
  number: string;
  date: Dayjs | null;
}

export const FactorSingleLimitPage = () => {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const backUrl = searchParams.get('backUrl');
  const { profile } = useAppSelector((state) => state.auth);
  const { data, isLoading } = useGetFactorSingleLimitQuery(id, { skip: !id });

  const [deleteGeneralAgreement] = useDeleteGeneralAgreementMutation();
  const [deleteLimit] = useDeleteFactorLimitMutation();

  const shouldFetchGeneralAgreement = !!data?.data?.generalAgreement?.didoxId;

  const { data: generalAgreement, isLoading: isGenerateLoading } =
    useGetGeneralAgreementV2Query(
      {
        buyer: data?.data.company.tin || '',
        amount: Number(data?.data.availableLimit?.toFixed(2) || 0),
        branchId: data?.data.branch.id || null,
        generalAgreementDocumentNumber:
          data?.data.generalAgreement?.number || '',
        generalAgreementDocumentDate: data?.data.generalAgreement?.date || null,
      },
      { skip: !shouldFetchGeneralAgreement }
    );

  const changeLimitModalRef = useRef<BaseModalRef>(null);
  const didoxHtmlModalRef = useRef<DidoxPreviewModalRef>(null);
  const [generatedFile, setGeneratedFile] = useState<File | null>(null);

  const { control, reset } = useForm<FormValues>({
    defaultValues: {
      number: '',
      date: null,
    },
  });

  useEffect(() => {
    if (
      data?.data.generalAgreement?.number &&
      data.data.generalAgreement?.date
    ) {
      reset({
        number: data.data.generalAgreement.number,
        date: dayjs(data.data.generalAgreement.date, 'DD.MM.YYYY')
          .format('DD.MM.YYYY')
          .toString(),
      });
    }
  }, [data, reset]);

  useEffect(() => {
    if (generalAgreement?.base64 && !generatedFile) {
      const base64String = generalAgreement.base64;
      const binaryString = atob(base64String);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const file = new File([blob], 'Генеральное_соглашение.pdf', {
        type: 'application/pdf',
      });
      setGeneratedFile(file);
    }
  }, [generalAgreement, generatedFile]);

  const handlePreviewDocument = () => {
    if (!data) return;

    if (data.data.generalAgreement?.didoxId) {
      didoxHtmlModalRef.current?.open({
        id: data.data.generalAgreement.didoxId,
        name: t('general_agreement'),
      });
    } else if (generatedFile) {
      didoxHtmlModalRef.current?.open({
        file: generatedFile,
        name: generatedFile.name,
      });
    }
  };

  return (
    <CabinetWrapper>
      {isLoading || !data ? (
        <UILoader />
      ) : (
        <>
          <PageTitle
            title={data.data.company.name || ''}
            backUrl={encodeURIComponent(backUrl || '/factor/requests')}
          />
          <FactorLimitActions
            sx={{ mt: 2.5 }}
            data={data.data}
            openLimitModal={() => {
              changeLimitModalRef.current?.open();
            }}
          />

          <Divider sx={{ my: 2.5 }} />

          <Stack direction='row' spacing={2}>
            <BlockComp
              title={t('set_limit')}
              value={numericFormatter(
                (data.data.availableLimit || 0).toFixed(2),
                {
                  thousandSeparator: ' ',
                }
              )}
            />
            <BlockComp
              title={t('used_limit')}
              value={numericFormatter((data.data.usedLimit || 0).toFixed(2), {
                thousandSeparator: ' ',
              })}
            />
            <BlockComp
              title={t('available_limit')}
              value={numericFormatter(
                (
                  (data.data.availableLimit || 0) + (data.data.usedLimit || 0)
                ).toFixed(2),
                {
                  thousandSeparator: ' ',
                }
              )}
            />
            <BlockComp
              title={t('earned_commission')}
              value={numericFormatter(
                (data?.data?.totalCommission || 0).toFixed(2),
                {
                  thousandSeparator: ' ',
                }
              )}
            />
          </Stack>

          <Stack direction='row' mt={2.5}>
            <UIDefinitionList
              term={t('status')}
              description={(() => {
                switch (data.data.status) {
                  case 'active':
                    return <Chip label={t('active')} color='success' />;
                  case 'overdue':
                    return <Chip label={t('overdue')} color='error' />;
                  case 'suspended':
                    return <Chip label={t('suspended')} color='info' />;
                  case 'generalAgreementSignatureExpected':
                    return (
                      <Chip
                        label={t(
                          'statuses.factor.generalAgreementSignatureExpected'
                        )}
                        sx={{
                          backgroundColor: '#FFEA00',
                          color: '#0B1370',
                        }}
                      />
                    );
                  case 'generalAgreementWasRejected':
                    return (
                      <Chip
                        label={t('statuses.factor.generalAgreementWasRejected')}
                        color='error'
                      />
                    );
                  case 'generalAgreementExpired':
                    return (
                      <Chip
                        label={t('statuses.factor.generalAgreementExpired')}
                        color='error'
                      />
                    );
                  default:
                    break;
                }
              })()}
            />

            <UIDefinitionList
              term={t('tin')}
              description={data.data.company.tin}
            />

            <UIDefinitionList
              term={t('set_limit_date')}
              description={data.data.createdAt}
            />

            {/* <UIDefinitionList
              term={t('set_limit_date')}
              description={data.data.}
            /> */}
          </Stack>
          <Stack direction='row' mt={2}>
            <UIDefinitionList
              term={t('branch')}
              description={data.data.branch.name}
            />
            <UIDefinitionList
              term={t('currency')}
              description={t(`currency_options.${data.data.currency}`)}
            />
          </Stack>

          {data.data.generalAgreement?.number &&
            data.data.generalAgreement?.date && (
              <>
                <Typography fontSize={17} fontWeight={600} mb={2}>
                  {t('general_agreement')}
                </Typography>
                <Box
                  sx={{
                    background: '#0B13700A',
                    p: '2.4rem',
                    borderRadius: '1.2rem',
                  }}
                >
                  <Stack direction='column'>
                    <Stack direction='row' spacing={2} mb={2}>
                      <Stack direction='column' spacing={1} width={'50%'}>
                        <UIFormLabel>
                          {t('payment_document.document_number')}:
                        </UIFormLabel>

                        <FormTextInput
                          control={control}
                          value={data.data.generalAgreement?.number}
                          name='number'
                          disabled={true}
                        />
                      </Stack>
                      <Stack direction='column' spacing={1}>
                        <UIFormLabel>
                          {t('payment_document.document_date')}:
                        </UIFormLabel>
                        <FormDatePicker
                          control={control}
                          name='date'
                          disabled={true}
                          value={dayjs(
                            data.data.generalAgreement?.date,
                            'DD.MM.YYYY'
                          )}
                        />
                      </Stack>
                    </Stack>

                    {data.data.generalAgreement?.didoxId && (
                      <>
                        <Stack
                          direction='row'
                          alignItems='center'
                          justifyContent='space-between'
                          sx={{
                            padding: '0.6rem 0.6rem 0.6rem 1.5rem',
                            background: '#FFFFFF',
                            borderRadius: '0.6rem',
                            border: '1px solid #E6F1FF',
                          }}
                        >
                          {isGenerateLoading ? (
                            <UILoader size={20} />
                          ) : (
                            <Typography
                              fontSize={14}
                              fontWeight={500}
                              color='#0B1370'
                            >
                              {t('Генеральное_соглашение.pdf')}
                            </Typography>
                          )}

                          <Stack
                            direction='row'
                            spacing={3}
                            alignItems='center'
                          >
                            <Button
                              color='inherit'
                              onClick={handlePreviewDocument}
                            >
                              {t('look')}
                            </Button>
                          </Stack>
                        </Stack>
                      </>
                    )}
                  </Stack>
                </Box>
              </>
            )}

          <UITabs
            sx={{ mt: 2.5 }}
            tabs={[
              {
                label: t('last_limit_change'),
                component: <FactorLimitActivities data={data.data} />,
              },
              // {
              //   label: (
              //     <UITabItemLabel
              //       label={t('updete_limit_history')}
              //       count={data.data.stats.totalDeals || 0}
              //     />
              //   ),
              //   component: <FactorLimitDeals />,
              // },
              // {
              //   label: (
              //     <UITabItemLabel
              //       label={t("transactions")}
              //       count={data.data.stats.totalTransactions || 0}
              //     />
              //   ),
              //   component: <FactorLimitTransactions />,
              // },
            ]}
          />
          <FactorChangeLimitModal ref={changeLimitModalRef} data={data.data} />
          <DidoxPreviewModal ref={didoxHtmlModalRef} showPdf showHtml={false} />
        </>
      )}
    </CabinetWrapper>
  );
};
